import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LoadingCard, LoadingStatsGrid } from "@xpense/ui";
import { api, MonthlySummary, Transaction, type MonthlyNutritionSummary } from "../api/client";
import { EmptyState, MetricCard, Page, RetryButton, Section } from "../components/Page";
import TransactionCard from "../components/TransactionCard";
import EditTransactionModal from "../components/EditTransactionModal";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";

export default function Dashboard() {
  const [summary, setSummary] = useState<MonthlySummary | null>(null);
  const [nutrition, setNutrition] = useState<MonthlyNutritionSummary | null>(null);
  const [recentTxns, setRecentTxns] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingTxn, setEditingTxn] = useState<Transaction | null>(null);
  const [deletingTxn, setDeletingTxn] = useState<Transaction | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const month = new Date().toISOString().slice(0, 7);
      const [summaryData, txnsData] = await Promise.all([
        api.get<MonthlySummary>(`/api/v1/summary/monthly?month=${month}`),
        api.get<any>("/api/v1/transactions?page_size=10"),
      ]);
      setSummary(summaryData);
      setRecentTxns(txnsData.data || []);
      api.get<MonthlyNutritionSummary>(`/api/v1/nutrition/monthly?month=${month}`)
        .then(setNutrition)
        .catch(() => setNutrition(null));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const formatINR = (amount: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);

  if (loading) {
    return (
      <Page
        eyebrow="Overview"
        title="Dashboard"
        description="Your month in motion: spend, income, confidence, and the transactions that need attention."
      >
        <LoadingStatsGrid />
        <div className="xpn-grid xpn-grid--2">
          <LoadingCard />
          <LoadingCard />
        </div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page eyebrow="Overview" title="Dashboard">
        <Section>
          <EmptyState
            icon="warning"
            title="Unable to load dashboard"
            description={error}
            action={<RetryButton onClick={loadData} />}
            tone="danger"
          />
        </Section>
      </Page>
    );
  }

  if (!summary || summary.transaction_count === 0) {
    return (
      <Page
        eyebrow="Overview"
        title="Dashboard"
        description="AI summaries will appear once transactions begin flowing in."
      >
        <Section>
          <EmptyState
            icon="plus"
            title="No transactions yet"
            description="Submit an SMS, notification, or manual entry to start building your expense intelligence."
            action={
              <Link className="xpn-btn xpn-btn--primary" to="/transactions">
                Add Transaction
              </Link>
            }
          />
        </Section>
      </Page>
    );
  }

  const netTone = summary.net >= 0 ? "success" : "danger";
  const expenseCategories = summary.categories.filter(
    (cat) => cat.category.toLowerCase() !== "income" && cat.total > 0
  );
  const topCategory = expenseCategories[0];
  const monthLabel = new Date(`${summary.month}-01T00:00:00`).toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });

  return (
    <Page
      eyebrow={monthLabel}
      title="Financial command center"
      description="A concise view of spending, income, AI confidence, and the latest money movement."
      action={
        <Link className="xpn-btn xpn-btn--secondary" to="/transactions">
          View Transactions
        </Link>
      }
    >
      <div className="xpn-grid xpn-grid--4">
        <MetricCard
          label="Spent this month"
          value={formatINR(summary.total_expense)}
          caption={topCategory ? `${topCategory.category} leads spend` : "No spend category data"}
          tone="danger"
        />
        <MetricCard
          label="Income"
          value={formatINR(summary.total_income)}
          caption="Recorded credits"
          tone="success"
        />
        <MetricCard
          label="Net position"
          value={formatINR(summary.net)}
          caption={summary.net >= 0 ? "You are ahead this month" : "Spend is outrunning income"}
          tone={netTone}
        />
        <MetricCard
          label="Transactions"
          value={summary.transaction_count}
          caption="Classified events"
          tone="info"
        />
      </div>

      <div className="xpn-grid xpn-grid--4">
        <MetricCard
          label="GST paid"
          value={formatINR(summary.tax_summary.gst_total)}
          caption={`${summary.tax_summary.items_needing_review} items need review`}
          tone={summary.tax_summary.items_needing_review > 0 ? "warning" : "info"}
        />
        <MetricCard
          label="Cess paid"
          value={formatINR(summary.tax_summary.cess_total)}
          caption="Compensation cess and similar levies"
          tone="neutral"
        />
        <MetricCard
          label="Other taxes"
          value={formatINR(summary.tax_summary.other_tax_total)}
          caption="VAT, excise, stamp duty, TCS/TDS, local tax"
          tone="neutral"
        />
        <MetricCard
          label="Total tax paid"
          value={formatINR(summary.tax_summary.total_tax_paid)}
          caption={`Taxable spend ${formatINR(summary.tax_summary.taxable_spend)}`}
          tone="info"
        />
      </div>

      <div className="xpn-grid xpn-grid--4">
        <MetricCard
          label="Nutrition score"
          value={nutrition?.health_score ?? "—"}
          caption="Based on food items this month"
          tone={nutrition?.health_score != null && nutrition.health_score >= 70 ? "success" : nutrition?.health_score != null && nutrition.health_score >= 40 ? "warning" : "danger"}
        />
        <MetricCard
          label="Healthy items"
          value={nutrition?.healthy_count ?? 0}
          caption="Protein, fruits, veggies, balanced"
          tone="success"
        />
        <MetricCard
          label="Unhealthy items"
          value={nutrition?.unhealthy_count ?? 0}
          caption="Junk, high sugar, high fat"
          tone={nutrition && nutrition.unhealthy_count > 0 ? "danger" : "neutral"}
        />
        <MetricCard
          label="Alcohol / Tobacco"
          value={`${nutrition?.alcohol_count ?? 0} / ${nutrition?.tobacco_count ?? 0}`}
          caption="Detected this month"
          tone={nutrition && (nutrition.alcohol_count > 0 || nutrition.tobacco_count > 0) ? "warning" : "neutral"}
        />
      </div>

      <div className="xpn-grid xpn-grid--2">
        <Section
          title="Spending by category"
          description="Top categories ranked by share of monthly spend."
        >
          <div className="xpn-section-body">
            {expenseCategories.length > 0 ? (
              expenseCategories.slice(0, 6).map((cat) => {
                const displayedPercentage = Math.max(0, Math.min(100, cat.percentage));
                return (
                  <div className="xpn-category-row" key={cat.category}>
                    <div>
                      <div className="xpn-flex-between xpn-mb-xs">
                        <span className="xpn-text-body-md xpn-text" style={{ fontWeight: 650 }}>
                          {cat.category}
                        </span>
                        <span data-tnum className="xpn-text-caption-tabular xpn-text-muted">
                          {displayedPercentage.toFixed(0)}%
                        </span>
                      </div>
                      <div className="xpn-progress">
                        <div
                          className="xpn-progress-bar"
                          style={{
                            width: `${Math.max(displayedPercentage, 3)}%`,
                            background: "var(--xpn-color-primary)",
                          }}
                        />
                      </div>
                      <div className="xpn-text-caption xpn-text-muted xpn-mt-sm">
                        {cat.count} transactions
                      </div>
                    </div>
                    <div data-tnum className="xpn-text-body-tabular xpn-text">
                      {formatINR(cat.total)}
                    </div>
                  </div>
                );
              })
            ) : (
              <EmptyState title="No category data" description="Categories appear after classification." />
            )}
          </div>
        </Section>

        <Section title="Top merchants" description="Where the largest recurring spend is landing.">
          <div className="xpn-section-body">
            {summary.top_merchants.length > 0 ? (
              summary.top_merchants.slice(0, 6).map((merchant) => (
                <div className="xpn-category-row" key={merchant.merchant}>
                  <div>
                    <div className="xpn-text-body-md xpn-text" style={{ fontWeight: 650 }}>
                      {merchant.merchant || "Unknown merchant"}
                    </div>
                    <div className="xpn-text-caption xpn-text-muted">{merchant.count} transactions</div>
                  </div>
                  <div data-tnum className="xpn-text-body-tabular xpn-text">
                    {formatINR(merchant.total)}
                  </div>
                </div>
              ))
            ) : (
              <EmptyState title="No merchant data" description="Merchant trends appear with more transactions." />
            )}
          </div>
        </Section>
      </div>

      <Section
        title="Recent transactions"
        description="Latest classified activity across SMS, notifications, and manual entries."
        action={
          <Link className="xpn-btn xpn-btn--secondary xpn-btn--sm" to="/transactions">
            See all
          </Link>
        }
      >
        {recentTxns.length === 0 ? (
          <EmptyState title="No recent transactions" description="New transactions will appear here first." />
        ) : (
          <div className="xpn-row-list">
            {recentTxns.slice(0, 10).map((txn) => (
              <TransactionCard
                key={txn.id}
                transaction={txn}
                onEdit={setEditingTxn}
                onDelete={setDeletingTxn}
              />
            ))}
          </div>
        )}
      </Section>

      {editingTxn && (
        <EditTransactionModal
          transaction={editingTxn}
          open={!!editingTxn}
          onClose={() => setEditingTxn(null)}
          onSaved={(updated) => {
            setRecentTxns((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
            setEditingTxn(null);
          }}
        />
      )}

      {deletingTxn && (
        <ConfirmDeleteModal
          transaction={deletingTxn}
          open={!!deletingTxn}
          onClose={() => setDeletingTxn(null)}
          onDeleted={(id) => {
            setRecentTxns((prev) => prev.filter((t) => t.id !== id));
            setDeletingTxn(null);
          }}
        />
      )}
    </Page>
  );
}
